/**
 * Utilidades para descubrimiento de capas ArcGIS REST cuando discoverLayer
 * no puede resolver la URL (p. ej. carpeta y servicio con el mismo nombre).
 */

const ARCGIS_REST_SERVICE =
    /\/rest\/services\/.+\/(MapServer|FeatureServer|ImageServer)\/?$/i;

/**
 * Extrae una URL http(s) válida del input del usuario.
 * Corrige pegados accidentales con rutas Windows (C:\\...\\https:\\host\\path).
 */
export function sanearUrlServicioDiscover(raw: string): string {
    if (raw == null || raw === '') {
        return '';
    }
    let value = String(raw).trim();

    // Ej: C:\proyecto\https:\srvags.sgc.gov.co\arcgis\rest\...
    const gluedBackslash = value.match(/(?:^|[\\/])(https?):\\+(.+)$/i);
    if (gluedBackslash) {
        const scheme = gluedBackslash[1].toLowerCase();
        const path = gluedBackslash[2].replace(/\\/g, '/');
        value = `${scheme}://${path}`;
    }

    const embedded = value.match(/https?:\/\/[^\s"'<>]+/i);
    if (embedded) {
        value = embedded[0];
    }

    value = value.replace(/\\/g, '/');
    value = value.replace(/([^:]\/)\/+/g, '$1');
    return value.trim();
}

export function esUrlServicioValida(url: string): boolean {
    return /^https?:\/\/.+/i.test((url || '').trim());
}

/** Trim + colapsa "//" sin quitar segmentos válidos de la ruta ArcGIS. */
export function normalizarUrlDiscover(url: string): string {
    if (url == null || url === '') {
        return url;
    }
    return sanearUrlServicioDiscover(url);
}

export function esUrlArcGisRest(url: string): boolean {
    if (!url) {
        return false;
    }
    return ARCGIS_REST_SERVICE.test(normalizarUrlDiscover(url));
}

/** URL base del servicio sin slash final (convención al asociar). */
export function normalizarUrlServicioArcGis(url: string): string {
    return normalizarUrlDiscover(url).replace(/\/+$/, '');
}

export function respuestaDiscoverTieneCapas(respuesta: any): boolean {
    return Array.isArray(respuesta?.layers) && respuesta.layers.length > 0;
}

/** True si el JSON es metadata ArcGIS usable (no error del proxy). */
export function esMetadataArcGisValida(metadata: any): boolean {
    if (!metadata || typeof metadata !== 'object') {
        return false;
    }
    if (metadata.status === 'error' || metadata.error) {
        return false;
    }
    return Array.isArray(metadata.layers) && metadata.layers.length > 0;
}

/**
 * Construye el payload esperado por LlenarListaCapas a partir del ?f=json del MapServer.
 * Replica la forma mínima que devuelve discoverLayer para capas no asociadas.
 */
export function construirDiscoverDesdeMetadataArcGis(
    metadata: any,
    serviceUrl: string
): { state: string; layers: any[]; message: string } {
    const base = normalizarUrlServicioArcGis(serviceUrl);
    const serviceName = (metadata?.mapName || extraerNombreServicio(base) || 'Map').trim();
    const serviceType = base.toLowerCase().includes('featureserver') ? 'esriFeature' : 'esriDynamic';
    const capaType = serviceType;

    const layers = (metadata?.layers || []).map((layer: any) => ({
        serviceName,
        serviceUrl: base,
        serviceType,
        serviceCode: '',
        code: '',
        id: String(layer.id),
        name: layer.name,
        alias: layer.name,
        type: capaType,
        url: `${base}/${layer.id}`,
        category: '',
        state: false,
        public: false,
        private: false,
        error: false,
        model: '',
        alertLocation: '',
        alertEndpoint: '',
        alertFrecuency: '',
        historical: '',
        statistical: '',
        order: layer.id,
        legend: '',
        downloadable: false,
        infoType: '',
        info: '',
    }));

    return {
        state: layers.length > 0 ? 'success' : 'fail',
        layers,
        message: layers.length > 0 ? 'success' : '',
    };
}

/**
 * Token para filterLayers (nombre del servicio en la ruta ArcGIS).
 * Ej: .../Mapa_Geologico_Colombia/MapServer → Mapa_Geologico_Colombia
 */
export function extraerTokenFiltroServicio(serviceUrl: string): string {
    return extraerNombreServicio(normalizarUrlServicioArcGis(serviceUrl || '')) || '';
}

function claveCapaUrl(url: string): string {
    return normalizarUrlServicioArcGis(url || '').toLowerCase();
}

/**
 * Overlay de capas ya asociadas (BD) sobre el catálogo ArcGIS del ?f=json.
 * Match por URL de capa o por serviceUrl + id.
 */
export function fusionarDiscoverConAsociadas(
    catalogo: { state: string; layers: any[]; message: string },
    asociadas: any[]
): { state: string; layers: any[]; message: string } {
    const baseLayers = Array.isArray(catalogo?.layers) ? catalogo.layers : [];
    const assoc = Array.isArray(asociadas) ? asociadas : [];
    if (baseLayers.length === 0 || assoc.length === 0) {
        return catalogo;
    }

    const porUrl = new Map<string, any>();
    const porServicioId = new Map<string, any>();

    assoc.forEach((capa) => {
        if (!capa) {
            return;
        }
        const code = capa.code != null ? String(capa.code) : '';
        const asociada = capa.state === true || code.indexOf('CAP_') === 0;
        if (!asociada) {
            return;
        }
        const urlKey = claveCapaUrl(capa.url);
        if (urlKey) {
            porUrl.set(urlKey, capa);
        }
        const ser = claveCapaUrl(capa.serviceUrl);
        const id = capa.id != null ? String(capa.id) : '';
        if (ser && id !== '') {
            porServicioId.set(`${ser}::${id}`, capa);
        }
    });

    const layers = baseLayers.map((layer) => {
        const urlKey = claveCapaUrl(layer.url);
        const ser = claveCapaUrl(layer.serviceUrl);
        const id = layer.id != null ? String(layer.id) : '';
        const match = porUrl.get(urlKey) || (ser && id ? porServicioId.get(`${ser}::${id}`) : null);
        if (!match) {
            return layer;
        }
        return {
            ...layer,
            ...match,
            // Conserva catálogo remoto si BD no trae nombre/url
            name: match.name || layer.name,
            url: match.url || layer.url,
            serviceUrl: match.serviceUrl || layer.serviceUrl,
            serviceName: match.serviceName || layer.serviceName,
            serviceType: match.serviceType || layer.serviceType,
            type: match.type || layer.type,
            id: match.id != null && match.id !== '' ? match.id : layer.id,
            state: true,
            code: match.code || layer.code,
        };
    });

    return {
        state: layers.length > 0 ? 'success' : catalogo.state,
        layers,
        message: catalogo.message || 'success',
    };
}

function extraerNombreServicio(serviceUrl: string): string {
    const match = serviceUrl.match(/\/services\/(?:[^/]+\/)?([^/]+)\/(MapServer|FeatureServer|ImageServer)/i);
    return match?.[1] || '';
}
